/**
 * Route Configuration
 *
 * Centralized route definitions to reduce repetition and improve maintainability.
 * This module defines all application routes and their configuration.
 *
 * @module routes
 */

import { ComponentType } from 'react';
import Dashboard from '@/pages/Dashboard';
import LandingPage from '@/pages/LandingPage';
import LearningHub from '@/pages/learning/LearningHub';
import GrammarOverview from '@/pages/learning/GrammarOverview';
import CommonSentencesOverview from '@/pages/learning/CommonSentencesOverview';
import GrammarTopicDetail from '@/pages/learning/GrammarTopicDetail';
import PDFBooks from '@/pages/learning/PDFBooks';
import PartnerFinder from '@/pages/PartnerFinder';
import ExamSimulation from '@/pages/ExamSimulation';
import Feedback from '@/pages/Feedback';
import SignIn from '@/pages/SignIn';
import SignUp from '@/pages/SignUp';
import Results from '@/pages/Results';
import ModuleResultReview from '@/pages/ModuleResultReview';
import HumanReviewsPage from '@/pages/HumanReviewsPage';
import DiagnosticProfilePage from '@/pages/DiagnosticProfilePage';
import Settings from '@/pages/Settings';
import ConsentPage from '@/pages/ConsentPage';
import PrivacyPolicy from '@/pages/legal/PrivacyPolicy';
import Terms from '@/pages/legal/Terms';
import Imprint from '@/pages/legal/Imprint';
import { ROUTES } from './routeConstants';

/**
 * Route configuration interface.
 *
 * Defines the structure for route definitions including path, component, and
 * authentication requirements.
 */
export interface RouteConfig {
  path: string;
  component: ComponentType;
  requiresAuth: boolean;
  /** When true, ConsentGate wraps the route (requires valid consent record). */
  requiresConsent?: boolean;
}

/**
 * Public routes (no authentication required)
 */
export const publicRoutes: RouteConfig[] = [
  {
    path: ROUTES.LANDING,
    component: LandingPage,
    requiresAuth: false,
  },
  {
    path: ROUTES.SIGN_IN,
    component: SignIn,
    requiresAuth: false,
  },
  {
    path: ROUTES.SIGN_UP,
    component: SignUp,
    requiresAuth: false,
  },
  {
    path: ROUTES.PRIVACY,
    component: PrivacyPolicy,
    requiresAuth: false,
  },
  {
    path: ROUTES.TERMS,
    component: Terms,
    requiresAuth: false,
  },
  {
    path: ROUTES.IMPRINT,
    component: Imprint,
    requiresAuth: false,
  },
];

export const protectedRoutes: RouteConfig[] = [
  {
    path: ROUTES.CONSENT,
    component: ConsentPage,
    requiresAuth: true,
    requiresConsent: false,
  },
  {
    path: ROUTES.DASHBOARD,
    component: Dashboard,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.SETTINGS,
    component: Settings,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.LEARNING,
    component: LearningHub,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.LEARNING_GRAMMAR,
    component: GrammarOverview,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.LEARNING_SENTENCES,
    component: CommonSentencesOverview,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.LEARNING_BOOKS,
    component: PDFBooks,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.LEARNING_GRAMMAR_TOPIC,
    component: GrammarTopicDetail,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.PARTNERS,
    component: PartnerFinder,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.PARTNERS_FIND,
    component: PartnerFinder,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.PARTNERS_REQUEST,
    component: PartnerFinder,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.PARTNERS_REQUEST_EDIT,
    component: PartnerFinder,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.PARTNERS_MESSAGES,
    component: PartnerFinder,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.EXAM,
    component: ExamSimulation,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.RESULTS,
    component: Results,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.RESULTS_REVIEW,
    component: ModuleResultReview,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.HUMAN_REVIEWS,
    component: HumanReviewsPage,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.DIAGNOSTIC_PROFILE,
    component: DiagnosticProfilePage,
    requiresAuth: true,
    requiresConsent: true,
  },
  {
    path: ROUTES.FEEDBACK,
    component: Feedback,
    requiresAuth: true,
    requiresConsent: true,
  },
];

/**
 * All routes combined (public + protected)
 */
export const allRoutes: RouteConfig[] = [...publicRoutes, ...protectedRoutes];
